import { IJobType } from '@/redux/post/post.d';
import { isCadExt } from '@/redux/post/post.constants';
import { V2Basics, V2File, V2JobDetails, V2Operation } from '../v2Upload.d';
import { OpChecklist, opChecklist } from './fileHelpers';

/** One unmet requirement: how to say it, and where to send the user to fix it.
 * `field` is a `data-focus` attribute, omitted when there is nothing to point at. */
export interface Missing {
  label: string;
  step: number;
  field?: string;
}

const collect = (
  checks: Array<[boolean, string, string]>,
  step: number
): Missing[] =>
  checks
    .filter(([bad]) => bad)
    .map(([, label, field]) => ({ label, step, field }));

/** The draft the backend creates on leaving Basics needs all five of these. */
export function missingForDraft(basics: V2Basics): Missing[] {
  return collect(
    [
      [!basics.title.trim(), 'a project name', 'title'],
      [!basics.description.trim(), 'a description', 'description'],
      [!basics.category.trim(), 'a category', 'category'],
      [!basics.difficulty.trim(), 'a difficulty', 'difficulty'],
      [basics.tags.length === 0, 'at least one tag', 'tags'],
    ],
    0
  );
}

/** A multi workpiece has no files of its own — a name, a category and the
 * projects it bundles are the whole gate. One project is not a set, so two is
 * the floor. */
export const MIN_GROUP_PROJECTS = 2;

export function missingForGroup(
  basics: V2Basics,
  selectedProjectIds: string[]
): Missing[] {
  return collect(
    [
      [!basics.title.trim(), 'a name for the group', 'title'],
      [!basics.description.trim(), 'a description', 'description'],
      [!basics.category.trim(), 'a category', 'category'],
      [!basics.difficulty.trim(), 'a difficulty', 'difficulty'],
      [basics.tags.length === 0, 'at least one tag', 'tags'],
      [
        selectedProjectIds.length < MIN_GROUP_PROJECTS,
        `at least ${MIN_GROUP_PROJECTS} projects`,
        'projects',
      ],
    ],
    0
  );
}

/** Editing a published group: the same metadata gate as authoring one, minus
 * anything about the children. Membership is still floored at two — a group of
 * one is a single project. */
export function missingForGroupEdit(
  details: Pick<
    V2Basics,
    'title' | 'description' | 'category' | 'difficulty' | 'tags'
  >,
  selectedProjectIds: string[]
): Missing[] {
  return collect(
    [
      [!details.title.trim(), 'a name for the group', 'title'],
      [!details.description.trim(), 'a description', 'description'],
      [!details.category.trim(), 'a category', 'category'],
      [!details.difficulty.trim(), 'a difficulty', 'difficulty'],
      [details.tags.length === 0, 'at least one tag', 'tags'],
      [
        selectedProjectIds.length < MIN_GROUP_PROJECTS,
        `at least ${MIN_GROUP_PROJECTS} projects`,
        'projects',
      ],
    ],
    0
  );
}

/** Photos, G-code and a CAD source are required; vectors, documentation and the
 * video links all ride along optionally. All three point at the drop zone — the
 * grid for a kind you have none of isn't rendered, so there is nothing else to
 * send the user to. */
export function missingForFiles(
  files: V2File[],
  orderConfirmed = true
): Missing[] {
  const has = (kind: V2File['kind']) => files.some((f) => f.kind === kind);
  // Not `has('doc')`: a README is a doc too, so this goes by extension.
  const hasCad = files.some((f) => isCadExt(f.ext));
  const checks: Array<[boolean, string]> = [
    [!has('image'), 'at least one photo'],
    [!has('gcode'), 'at least one G-code file'],
    [
      !hasCad,
      'a CAD source file (.crv, .art, .c2d, .stl, .step… — an SVG or DXF ' +
        'does not count)',
    ],
  ];
  const gaps: Missing[] = checks
    .filter(([bad]) => bad)
    .map(([, label]) => ({ label, step: 1, field: 'dropzone' }));
  if (has('gcode') && !orderConfirmed) {
    gaps.push({
      label: 'the carve order confirmed',
      step: 1,
      field: 'order-confirmed',
    });
  }
  return gaps;
}

const shortMissing = (c: OpChecklist): string => {
  if (!c.hasBit) return 'needs a bit or laser';
  if (!c.hasSpeeds) return 'needs a spindle speed';
  return 'needs a G-code file';
};

export function missingForTools(operations: V2Operation[]): Missing[] {
  if (operations.length === 0) {
    return [
      {
        label: 'at least one G-code file to assign',
        step: 2,
        field: 'tool-groups',
      },
    ];
  }
  return operations
    .map((op) => ({ op, check: opChecklist(op) }))
    .filter(({ check }) => !check.complete)
    .map(({ op, check }) => ({
      label: `${op.name} — ${shortMissing(check)}`,
      step: 2,
      field: `op-${op.id}`,
    }));
}

/** Every field `createPostDto` declares required. UNITS is required too and
 * deliberately absent — it comes from the G-code, with no control to point at. */
export function missingForSubmit(jd: V2JobDetails): Missing[] {
  // Rotary needs DIFFERENT dimensions, not fewer
  const dims =
    jd.jobType === IJobType.ROTARY
      ? [jd.length, jd.diameter]
      : [jd.width, jd.height, jd.thickness];

  return collect(
    [
      [
        !dims.every((v) => Number(v) > 0),
        jd.jobType === IJobType.ROTARY ? 'the stock size' : 'the job size',
        'jobSize',
      ],
      [!jd.jobType, 'a job type', 'jobType'],
      [
        jd.jobType === IJobType.DOUBLE_SIDED && !jd.flipDirection,
        'a flip direction',
        'flipDirection',
      ],
      [!jd.zZeroPosition, 'a Z-zero position', 'zZero'],
      [!jd.xyDatumPosition, 'an XY datum', 'xyDatum'],
      [
        jd.softwareUsed.length === 0,
        'at least one design software',
        'software',
      ],
    ],
    3
  );
}

/** Every gate in the wizard, for the last step. */
export function missingForSignoff(
  basics: V2Basics,
  files: V2File[],
  operations: V2Operation[],
  jobDetails: V2JobDetails
): Missing[] {
  return [
    ...missingForDraft(basics),
    ...missingForFiles(files),
    ...missingForTools(operations),
    ...missingForSubmit(jobDetails),
  ];
}
